import { Injectable } from '@angular/core';
import { Routes, Route } from '@angular/router';
import { Type } from '@angular/core';
import { canActivate } from '@auth/guards/auth.guard';
import { PageDataResolver } from '@app/resolvers/page-data.resolver';
import { RawRoute } from '@interfaces/raw-route';

/**
 * Maps guard key strings (from the backend route config) to their corresponding
 * Angular route guard functions. Add new guards here as the application grows.
 */
const GUARD_REGISTRY: Record<string, unknown> = {
  authGuard: canActivate,
};

/**
 * Maps resolver key strings (from the backend route config) to their corresponding
 * Angular resolver classes. Add new resolvers here as the application grows.
 */
const RESOLVER_REGISTRY: Record<string, unknown> = {
  PageDataResolver,
};

/**
 * Lazy-loading factory for an Angular standalone component.
 * Returns either a direct component type or a default-export wrapper.
 */
type ComponentLoader = () => Promise<
  Type<unknown> | { default: Type<unknown> }
>;

/**
 * Maps componentKey strings (from DB) to lazy-loaded component factories.
 * This is the ONLY frontend-specific mapping needed – everything else
 * comes from the backend as-is.
 */
const COMPONENT_REGISTRY: Record<string, ComponentLoader> = {
  MenuPage: () => import('@components/menu/menu.page').then(m => m.MenuPage),
  LoginPage: () =>
    import('@components/login/login.page').then(m => m.LoginPage),
  UserAccessPage: () =>
    import('@components/user-access/user-access.page').then(
      m => m.UserAccessPage
    ),
  PagePage: () => import('@components/page/page.page').then(m => m.PagePage),
  HelpPage: () => import('@components/help/help.page').then(m => m.HelpPage),
  AdministrationPage: () =>
    import('@components/administration/administration.page').then(
      m => m.AdministrationPage
    ),
  UserManagementPage: () =>
    import('@components/administration/user-management/user-management.page').then(
      m => m.UserManagementPage
    ),
  RoleManagementPage: () =>
    import('@components/administration/role-management/role-management.page').then(
      m => m.RoleManagementPage
    ),
  DataimportPage: () =>
    import('@components/administration/dataimport/dataimport.page').then(
      m => m.DataimportPage
    ),
  ImpressumPage: () =>
    import('@components/impressum/impressum.page').then(m => m.ImpressumPage),
  ErrorPage: () =>
    import('@components/error/error.page').then(m => m.ErrorPage),
};

/**
 * Converts raw backend route definitions into Angular {@link Routes}.
 * All Angular-specific wiring (guards, resolvers, component loaders) is
 * handled here; the HTTP layer stays separate in {@link RouteClientService}.
 */
@Injectable({ providedIn: 'root' })
export class RouteMapperService {
  /**
   * Recursively converts an array of {@link RawRoute} objects into Angular
   * {@link Routes}.
   *
   * @param raw - The raw route array received from the backend.
   * @returns A filtered list of valid Angular {@link Route} objects.
   */
  buildRoutes(raw: RawRoute[]): Routes {
    return (raw ?? []).flatMap(r => {
      const route = this.toRoute(r);
      return route ? [route] : [];
    });
  }

  /**
   * Converts a single {@link RawRoute} into an Angular {@link Route}.
   * Redirect routes are returned immediately. For component routes the
   * {@link COMPONENT_REGISTRY} is consulted; if no matching loader is found,
   * a warning is logged and `null` is returned so the route is skipped.
   * Guards, resolvers and child routes are wired up via their respective
   * registries.
   *
   * @param raw - A single raw route definition from the backend.
   * @returns A configured Angular {@link Route}, or `null` if the route is invalid.
   */
  private toRoute(raw: RawRoute): Route | null {
    if (raw.redirectTo) {
      return { path: raw.path, redirectTo: raw.redirectTo };
    }

    const loader = raw.componentKey
      ? COMPONENT_REGISTRY[raw.componentKey]
      : undefined;

    if (!loader) {
      console.warn(
        `Unknown componentKey "${raw.componentKey}" – route skipped.`
      );
      return null;
    }

    const route: Route = { path: raw.path, loadComponent: loader };

    if (raw.data) route.data = raw.data;
    if (raw.runGuardsAndResolvers)
      route.runGuardsAndResolvers = raw.runGuardsAndResolvers as any;

    if (raw.canActivate?.length) {
      const guards = raw.canActivate
        .map(key => {
          const guard = GUARD_REGISTRY[key];
          if (!guard) console.warn(`Unknown guard "${key}" – ignored.`);
          return guard;
        })
        .filter(Boolean);

      if (guards.length) route.canActivate = guards as any[];
    }

    if (raw.resolve && Object.keys(raw.resolve).length) {
      route.resolve = Object.fromEntries(
        Object.entries(raw.resolve).flatMap(([k, v]) => {
          const resolver = RESOLVER_REGISTRY[v];
          if (!resolver) return [];
          return [[k, resolver]];
        })
      );
    }

    if (raw.children?.length) {
      route.children = this.buildRoutes(raw.children);
    }

    return route;
  }
}
